import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ChartsLegendItem } from 'components/atoms/charts';

import s from './styles.module.scss';

import type { IPieChartData } from 'components/atoms/charts/types';

interface IProps {
  total: number;
  data: IPieChartData[];
  compact?: boolean;
}

interface ISingleNode {
  type: 'single';
  datum: IPieChartData;
}

interface IGroupNode {
  type: 'group';
  groupId: string;
  groupLabel: string;
  groupColor: string;
  total: number;
  children: IPieChartData[];
}

type TLegendNode = ISingleNode | IGroupNode;

const LegendList: React.FC<IProps> = ({ data, total, compact }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const swatchSize = compact ? { width: '10px', height: '10px' } : {};

  const getPercent = (val: number) => {
    if (total === 0) return 0;
    return ((val / total) * 100).toFixed(2);
  };

  // Collapse data points that share a `groupId` into one summary node, keeping
  // the first occurrence's position in the reading order.
  const nodes = useMemo<TLegendNode[]>(() => {
    const result: TLegendNode[] = [];
    const groupIndexById = new Map<string, number>();

    data.forEach((d) => {
      if (!d.groupId) {
        result.push({ type: 'single', datum: d });
        return;
      }
      const existing = groupIndexById.get(d.groupId);
      if (existing === undefined) {
        result.push({
          type: 'group',
          groupId: d.groupId,
          groupLabel: d.groupLabel ?? d.name,
          groupColor: d.groupColor ?? d.color,
          total: d.value,
          children: [d],
        });
        groupIndexById.set(d.groupId, result.length - 1);
      } else {
        const group = result[existing] as IGroupNode;
        group.children.push(d);
        group.total += d.value;
      }
    });

    return result;
  }, [data]);

  const toggle = (groupId: string) => {
    setExpanded((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  return (
    <ul className={classNames(s.list, { [s.compact]: compact })}>
      <li>{t('labels.legend')}</li>

      {nodes.map((node, i) => {
        if (node.type === 'single') {
          const { datum } = node;
          return (
            <li key={i}>
              <ChartsLegendItem
                name={`${datum.name} (${datum.value})`}
                color={datum.color}
                {...swatchSize}
              />
              {`${getPercent(datum.value)}%`}
            </li>
          );
        }

        const isOpen = !!expanded[node.groupId];
        return (
          <React.Fragment key={i}>
            <li>
              <button
                type="button"
                className={s.groupToggle}
                aria-expanded={isOpen}
                onClick={() => toggle(node.groupId)}
              >
                <ChartsLegendItem
                  name={`${node.groupLabel} (${node.total})`}
                  color={node.groupColor}
                  shape="triangle"
                  {...swatchSize}
                />
              </button>
              {`${getPercent(node.total)}%`}
            </li>

            {isOpen &&
              node.children.map((child, j) => (
                <li key={`${i}-${j}`} className={s.groupChild}>
                  <ChartsLegendItem
                    name={`${child.name} (${child.value})`}
                    color={child.color}
                    shape="triangle"
                    {...swatchSize}
                  />
                  {`${getPercent(child.value)}%`}
                </li>
              ))}
          </React.Fragment>
        );
      })}
    </ul>
  );
};

export default LegendList;
